import React from 'react';

// ── SVG Icons ──────────────────────────────────────────────────────────────────

const SignalIcon = ({ color = '#fff' }) => (
  <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
    <rect x="0" y="8" width="3" height="4" rx="1" fill={color} />
    <rect x="4.5" y="5" width="3" height="7" rx="1" fill={color} opacity="0.7" />
    <rect x="9" y="2" width="3" height="10" rx="1" fill={color} opacity="0.5" />
    <rect x="13.5" y="0" width="3" height="12" rx="1" fill={color} opacity="0.3" />
  </svg>
);

const WifiIcon = ({ color = '#fff' }) => (
  <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
    <path d="M8 9.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" fill={color} />
    <path d="M3.5 6.5C4.9 5.1 6.35 4.5 8 4.5s3.1.6 4.5 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
    <path d="M1 3.5C3 1.5 5.35.5 8 .5s5 1 7 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
  </svg>
);

const BatteryIcon = ({ color = '#fff' }) => (
  <svg width="25" height="12" viewBox="0 0 25 12" fill="none">
    <rect x="0.5" y="0.5" width="21" height="11" rx="3.5" stroke={color} strokeOpacity="0.35" />
    <rect x="2" y="2" width="17" height="8" rx="2" fill={color} />
    <path d="M23 4v4a2 2 0 000-4z" fill={color} fillOpacity="0.4" />
  </svg>
);

// ── Status Bar ─────────────────────────────────────────────────────────────────

const StatusBar = ({ dark, time = '9:41' }) => {
  const color = dark ? '#fff' : '#1a1a1a';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 20px 0',
        height: 44,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 600, color, letterSpacing: '-0.3px' }}>{time}</span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <SignalIcon color={color} />
        <WifiIcon color={color} />
        <BatteryIcon color={color} />
      </div>
    </div>
  );
};

// ── Dynamic Island ─────────────────────────────────────────────────────────────

const DynamicIsland = () => (
  <div
    style={{
      position: 'absolute',
      top: 12,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 126,
      height: 37,
      background: '#000',
      borderRadius: 20,
      zIndex: 10,
    }}
  />
);

// ── Home Indicator ─────────────────────────────────────────────────────────────

const HomeIndicator = ({ dark }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      paddingBottom: 8,
      paddingTop: 4,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: 134,
        height: 5,
        borderRadius: 3,
        background: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
      }}
    />
  </div>
);

// ── Navigation Bar ─────────────────────────────────────────────────────────────

const NavBar = ({ title, dark, showBackButton = false }) => {
  const color = dark ? '#fff' : '#1a1a1a';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        padding: '0 20px 8px',
        minHeight: 52,
        flexShrink: 0,
      }}
    >
      {showBackButton && (
        <div style={{ color: '#0a84ff', fontSize: 17, marginRight: 8 }}>‹ Back</div>
      )}
      <div
        style={{
          fontSize: 34,
          fontWeight: 700,
          color,
          letterSpacing: '-0.8px',
          lineHeight: 1.1,
        }}
      >
        {title}
      </div>
    </div>
  );
};

// ── iOS Keyboard ──────────────────────────────────────────────────────────────

export const IOSKeyboard = ({ dark = false }) => {
  const bg = dark ? 'rgba(20,20,20,0.85)' : 'rgba(240,240,240,0.85)';
  const keyBg = dark ? 'rgba(80,80,80,0.7)' : 'rgba(255,255,255,0.8)';
  const textColor = dark ? '#fff' : '#000';
  const rows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
    ['123', 'space', 'return'],
  ];

  return (
    <div
      style={{
        background: bg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        padding: '8px 4px',
        borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
      }}
    >
      {rows.map((row, ri) => (
        <div key={ri} style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
          {row.map((key) => (
            <div
              key={key}
              style={{
                background: keyBg,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: 5,
                minWidth: key === 'space' ? 180 : key.length > 1 ? 42 : 32,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: key === '123' || key === 'return' ? 13 : 16,
                fontWeight: 400,
                color: textColor,
                boxShadow: '0 1px 0 rgba(0,0,0,0.2)',
                userSelect: 'none',
                cursor: 'default',
              }}
            >
              {key === 'space' ? '' : key}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// ── iOS Settings List ──────────────────────────────────────────────────────────

export const IOSListRow = ({ label, value, chevron = true, icon, iconColor, dark }) => {
  const borderColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const textColor = dark ? '#fff' : '#1a1a1a';
  const subColor = dark ? 'rgba(255,255,255,0.4)' : '#888';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: `1px solid ${borderColor}`,
        background: 'transparent',
        gap: 12,
      }}
    >
      {icon && (
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 7,
            background: iconColor || '#0a84ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ flex: 1, fontSize: 16, color: textColor }}>{label}</div>
      {value && <div style={{ fontSize: 16, color: subColor }}>{value}</div>}
      {chevron && <div style={{ color: subColor, fontSize: 18 }}>›</div>}
    </div>
  );
};

export const IOSList = ({ children, dark, style }) => {
  const bg = dark ? 'rgba(255,255,255,0.05)' : '#fff';
  return (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        overflow: 'hidden',
        margin: '0 16px 16px',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// ── DeviceFrame ────────────────────────────────────────────────────────────────

/**
 * DeviceFrame — iOS 26-style device shell.
 *
 * Props:
 *   width, height — dimensions (default 390×844)
 *   dark — dark mode
 *   title — navigation bar large title
 *   showNavBar — whether to show the navigation bar (default false)
 *   showStatusBar — whether to show the status bar (default true)
 */
const DeviceFrame = ({
  width = 390,
  height = 844,
  dark = false,
  title,
  showNavBar = false,
  showStatusBar = true,
  children,
  style,
}) => {
  const bg = dark ? '#000' : '#f2f2f7';
  const bezelColor = dark ? '#1a1a1a' : '#e8e8e8';
  const borderColor = dark ? '#333' : '#ccc';

  return (
    <div
      style={{
        width,
        height,
        borderRadius: 54,
        background: bezelColor,
        border: `3px solid ${borderColor}`,
        boxShadow: dark
          ? '0 30px 80px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.05)'
          : '0 30px 80px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(255,255,255,0.8)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        ...style,
      }}
    >
      {/* Dynamic Island */}
      <DynamicIsland />

      {/* Screen */}
      <div
        style={{
          flex: 1,
          background: bg,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: '-apple-system, SF Pro Display, Helvetica Neue, sans-serif',
        }}
      >
        {showStatusBar && <StatusBar dark={dark} />}
        {showNavBar && title && <NavBar title={title} dark={dark} />}

        {/* Content area */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {children}
        </div>

        <HomeIndicator dark={dark} />
      </div>
    </div>
  );
};

export default DeviceFrame;
